import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, Clock } from 'lucide-react';
import { getProductBySlug, ctaLabel } from '../data/products';
import { useCart } from '../context/CartContext';
import { StockBadge } from '../components/StockBadge';
export function Compare() {
  const {
    addToCart
  } = useCart();
  const slugs = ['yarbo-lawn-mower', 'yarbo-lawn-mower-pro', 'yarbo-snow-blower', 'yarbo-blower', 'yarbo-all-season-pro-bundle'];
  const compareProducts = slugs.map(slug => getProductBySlug(slug)).filter((p): p is NonNullable<typeof p> => Boolean(p));
  const rows = [{
    label: 'Best for',
    values: ['Standard mowing', 'Heavy/steep mowing', 'Winter snow clearing', 'Leaf & debris clearing', 'Year-round automation']
  }, {
    label: 'Yard task',
    values: ['Lawn care', 'Lawn care (Pro)', 'Snow removal', 'Leaf cleanup', 'All-season']
  }, {
    label: 'Includes Yarbo Core?',
    values: ['Yes', 'Yes', 'Yes', 'Yes', 'Yes']
  }, {
    label: 'Module type',
    values: ['Lawn Mower', 'Lawn Mower Pro', 'Snow Blower', 'Blower', 'Pro + Snow + Blower']
  }];
  return <div className="bg-gray-50 min-h-screen" data-id="element-274">
      <div className="bg-white border-b border-gray-200" data-id="element-275">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-id="element-276">
          <h1 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-2" data-id="element-277">
            Compare Yarbo Systems
          </h1>
          <p className="text-gray-600 max-w-2xl" data-id="element-278">
            Side-by-side comparison of Yarbo complete systems and the all-season
            bundle.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-id="element-279">
        {/* Desktop table */}
        <div className="hidden lg:block bg-white rounded-2xl border border-gray-200 overflow-hidden" data-id="element-280">
          <table className="w-full" data-id="element-281">
            <thead data-id="element-282">
              <tr className="bg-gray-50 border-b border-gray-200" data-id="element-283">
                <th className="p-5 text-left text-xs font-bold uppercase tracking-wider text-gray-700 w-1/6" data-id="element-284">
                  Feature
                </th>
                {compareProducts.map((p, i) => <th key={p.id} className={`p-5 text-center ${i === 4 ? 'bg-yarbo-yellow/10' : ''}`} data-id="element-285">
                    <div className="aspect-square w-24 mx-auto mb-3 bg-gray-50 rounded-lg p-2" data-id="element-286">
                      <img src={p.image} alt={p.name} className="w-full h-full object-contain" data-id="element-287" />
                    </div>
                    <div className="text-sm font-bold text-yarbo-dark mb-1" data-id="element-288">
                      {p.name}
                    </div>
                    <div className="text-lg font-extrabold text-yarbo-dark mb-2" data-id="element-289">
                      ${p.price.toLocaleString()}
                    </div>
                    <StockBadge status={p.status} data-id="element-290" />
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-291">
              {rows.map((row, idx) => <tr key={idx} data-id="element-292">
                  <td className="p-5 font-bold text-gray-700 text-sm" data-id="element-293">
                    {row.label}
                  </td>
                  {row.values.map((val, i) => <td key={i} className={`p-5 text-center text-sm ${i === 4 ? 'bg-yarbo-yellow/5 font-medium' : 'text-gray-600'}`} data-id="element-294">
                      {val}
                    </td>)}
                </tr>)}
              <tr className="bg-gray-50" data-id="element-295">
                <td className="p-5" data-id="element-296"></td>
                {compareProducts.map((p, i) => <td key={p.id} className={`p-5 text-center ${i === 4 ? 'bg-yarbo-yellow/10' : ''}`} data-id="element-297">
                    <button onClick={() => addToCart(p)} className="w-full bg-yarbo-dark text-white font-bold text-sm py-3 rounded-lg hover:bg-black transition-colors flex items-center justify-center gap-2" data-id="element-298">
                      {p.status === 'in-stock' ? <ShoppingCart className="w-4 h-4" data-id="element-299" /> : <Clock className="w-4 h-4" data-id="element-300" />}
                      {ctaLabel(p.status)}
                    </button>
                    <Link to={`/product/${p.slug}`} className="block mt-2 text-xs font-bold text-gray-600 hover:text-yarbo-dark" data-id="element-301">
                      View Details →
                    </Link>
                  </td>)}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Mobile stacked */}
        <div className="lg:hidden space-y-4" data-id="element-302">
          {compareProducts.map((p, i) => <motion.div key={p.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.05
        }} className={`bg-white rounded-xl border p-5 ${i === 4 ? 'border-yarbo-yellow bg-yarbo-yellow/5' : 'border-gray-200'}`} data-id="element-303">
              <div className="flex gap-4 mb-4" data-id="element-304">
                <div className="w-20 h-20 bg-gray-50 rounded-lg p-2 flex-shrink-0" data-id="element-305">
                  <img src={p.image} alt={p.name} className="w-full h-full object-contain" data-id="element-306" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-307">
                  <h3 className="font-bold text-yarbo-dark mb-1" data-id="element-308">{p.name}</h3>
                  <div className="text-xl font-extrabold text-yarbo-dark mb-2" data-id="element-309">
                    ${p.price.toLocaleString()}
                  </div>
                  <StockBadge status={p.status} data-id="element-310" />
                </div>
              </div>
              <dl className="space-y-2 text-sm mb-4" data-id="element-311">
                {rows.map((row, idx) => <div key={idx} className="flex justify-between gap-3" data-id="element-312">
                    <dt className="text-gray-500" data-id="element-313">{row.label}</dt>
                    <dd className="text-gray-800 font-medium text-right" data-id="element-314">
                      {row.values[i]}
                    </dd>
                  </div>)}
              </dl>
              <div className="flex gap-2" data-id="element-315">
                <button onClick={() => addToCart(p)} className="flex-1 bg-yarbo-dark text-white font-bold py-3 rounded-lg text-sm flex items-center justify-center gap-2" data-id="element-316">
                  {p.status === 'in-stock' ? <ShoppingCart className="w-4 h-4" data-id="element-317" /> : <Clock className="w-4 h-4" data-id="element-318" />}
                  {ctaLabel(p.status)}
                </button>
                <Link to={`/product/${p.slug}`} className="flex-1 text-center text-yarbo-dark border border-gray-200 font-bold py-3 rounded-lg text-sm" data-id="element-319">
                  View Details
                </Link>
              </div>
            </motion.div>)}
        </div>
      </div>
    </div>;
}